import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Command } from 'lucide-react';
import { useRpc, refreshAll } from '../../lib/queries';
import type { AnalyticsAlert, Overview } from '../../lib/types';

const Pill: React.FC<{ to: string; tone: 'live' | 'off' | 'warn' | 'crit'; children: React.ReactNode; title?: string }> = ({ to, tone, children, title }) => (
  <Link
    to={to}
    title={title}
    className={[
      'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12.5px] font-medium whitespace-nowrap border',
      tone === 'live' ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-800'
        : tone === 'crit' ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-stone-200 bg-white text-stone-600',
    ].join(' ')}
  >
    {tone === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
    {children}
  </Link>
);

/** The always-visible system strip: switch states, emails sent today, refresh. No send cap: Resend is on a paid plan. */
export const TopStrip: React.FC<{ onOpenPalette: () => void }> = ({ onOpenPalette }) => {
  const { data: p } = useRpc<Overview>('admin_dash_overview', {}, { refetchInterval: 120_000 });
  const { data: alerts } = useRpc<AnalyticsAlert[]>('admin_analytics_alerts', {}, { refetchInterval: 300_000 });
  const real = (alerts ?? []).filter(a => a.severity !== 'info');
  const crit = real.filter(a => a.severity === 'crit').length;
  const [spinning, setSpinning] = useState(false);

  const refresh = async () => {
    setSpinning(true);
    await refreshAll();
    setTimeout(() => setSpinning(false), 600);
  };
  const sent = p ? p.emails_today.lifecycle + p.emails_today.alerts + p.emails_today.welcome : 0;

  return (
    <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-stone-200">
      <div className="max-w-[1440px] mx-auto px-8 h-12 flex items-center gap-2 overflow-x-auto">
        {real.length > 0 && (
          <Pill to="/" tone={crit ? 'crit' : 'warn'} title={real.map(a => a.message).join(' ')}>
            {real.length} alert{real.length > 1 ? 's' : ''}{crit ? `, ${crit} critical` : ''}
          </Pill>
        )}
        {p && (
          <>
            <Pill to="/alerts" tone={p.switches?.firm_alerts_live ? 'live' : 'off'}>
              Radar {p.switches?.firm_alerts_live ? 'armed' : 'paused'}
            </Pill>
            <Pill to="/email" tone={p.switches?.lifecycle_emails_live ? 'live' : 'off'}>
              Lifecycle emails {p.switches?.lifecycle_emails_live ? 'on' : 'off'}
            </Pill>
            <Pill to="/email" tone="off" title="Lifecycle, alert and welcome emails since London midnight">
              {sent.toLocaleString('en-GB')} emails sent today
            </Pill>
            {p.deletion_pending > 0 && (
              <Pill to="/compliance" tone="crit">
                {p.deletion_pending} deletion request{p.deletion_pending > 1 ? 's' : ''} pending
              </Pill>
            )}
            {p.dispatch_pending > 0 && (
              <Pill to="/alerts" tone="warn">{p.dispatch_pending} firm alerts queued</Pill>
            )}
          </>
        )}
        <div className="flex-1" />
        <button
          onClick={onOpenPalette}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-stone-200 bg-white text-[12.5px] font-medium text-stone-500 hover:text-stone-900 hover:border-stone-300 transition-colors"
        >
          <Command className="w-3.5 h-3.5" /> Search
          <span className="text-stone-400">cmd K</span>
        </button>
        <button
          onClick={refresh}
          title="Refresh all data"
          aria-label="Refresh all data"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-stone-200 bg-white text-stone-500 hover:text-stone-900 hover:border-stone-300 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${spinning ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};
